// The townsfolk in the sim (phase D1: the townsfolk and their routines for D1.3, the crowd for D1.5): one kernel module
// on the streamed valley. Each NPC follows its routine (@rpg/ai's routines on the schedule seam) by the sky's clock:
// home at night, work by day (the smith at the forge, the merchant at the stall), the square in the evening. It walks
// on the navmesh with its own @rpg/nav crowd (the hero and the fight's enemies placed in it for the others to avoid)
// while its ground's nav tile is built; when its chunk isn't resident (the hero far away) it is "virtual" and walks a
// straight line at its pace, unseen, until its tile is back. At its spot it faces the spot's way and does the
// activity; at an inside place (its home at night) it goes in and is out of sight. When the clock jumps (the hour set,
// a rest), everyone is put on their spots at once.
//
// Interaction: every NPC is an interactable in phase C's registry (the things' `Interaction`, talk above picking up),
// moved to where it stands each step; a press fires `npc:interact` (story's dialogue opens on it)
// and the NPC stops and faces the hero until `dialogue:close`. Facts (seam 2): `time.hours` and `npc.<id>.place`.
//
// One sim step (system `town:npcs`, order 55: after the fight's crowd at 50, before the physics step at 100).
// State: one kernel component (`town/State`: every NPC's place, routine, mode and talk), saved and hashed; the crowd
// rides the module's save slice (@rpg/nav's plain-data crowd: a restore continues bit for bit).
import type { GameModule, World } from "@rpg/kernel";
import { ROUTINE_FAULTS, RoutineSchedule, placeOf, stepRoutines, type RoutineState, type RoutineAgent } from "@rpg/ai";
import { NavCrowd, type CrowdSnapshot, type NavWorld } from "@rpg/nav";
import type { Interaction } from "@rpg/controller";
import type { GameEvents } from "../core";
import type { TownContent, NpcDef } from "./content";
import type { TownPlaces } from "./places";
import type { TownFacts } from "./facts";
import { isFresh } from "../save/fresh";

export const TOWN_C = "town/State";
/** The component types the state hash covers. */
export const TOWN_HASHED = [TOWN_C] as const;
/** The interaction registry's id prefix for NPCs. */
export const NPC_PREFIX = "npc:";
/** A clock jump past this many hours in one step puts everyone on their spots at once. */
export const SNAP_HOURS = 0.25;

/** Planted defects the townsfolk own. */
export const TOWN_FAULTS = {
  /** NPCs never leave the first block of their routine (`@rpg/ai` `ROUTINE_FAULTS.hourIgnored`): the at-hour check
   *  must go red. */
  routineHourIgnored: false,
};

/** One NPC in the sim. */
export interface NpcState {
  id: string;
  /** Feet. */
  pos: [number, number, number];
  yaw: number;
  /** Ground velocity (m/s), for the view's gait. */
  vel: [number, number];
  /** On the crowd, walking a straight line unseen, or indoors out of sight. */
  mode: "live" | "virtual" | "inside";
  routine: RoutineState;
  /** What it is doing at its spot now (null while it walks or talks). */
  activity: string | null;
  /** Talking with the hero: stopped, facing them, until the conversation closes (or a cap). */
  talk: { until: number; face: [number, number] } | null;
}

/** The townsfolk's whole state (a kernel component). */
export interface TownState {
  /** Sim seconds since the scene began. */
  t: number;
  /** The clock's hour at the last step (a jump past `SNAP_HOURS` snaps). */
  lastHour: number | null;
  npcs: NpcState[];
  /** Conversations started. */
  talks: number;
  /** Snaps so far. */
  snaps: number;
}

/** What the townsfolk need from the scene. */
export interface TownHost {
  content: TownContent;
  places: TownPlaces;
  nav: NavWorld;
  /** The sky's hour now. */
  hours(): number;
  /** The hero's feet and ground velocity. */
  hero(): { x: number; y: number; z: number; vx: number; vz: number };
  /** Other moving bodies the townsfolk step round (the fight's live enemies). */
  others(): { x: number; y: number; z: number }[];
  groundAt(x: number, z: number): number;
  /** The interaction registry NPCs join (phase C's, the things'). */
  interaction(): Interaction;
  facts: TownFacts;
  /** The talk's longest hold, if no `dialogue:close` comes (s). */
  talkCap?: number;
}

/** The townsfolk, for the scene, its view, the cutscenes and the handle. */
export interface Townsfolk {
  module: GameModule<GameEvents>;
  build(world: World): void;
  hashed: readonly string[];
  state(): TownState;
  npc(id: string): NpcState;
  def(id: string): NpcDef;
  schedule: RoutineSchedule;
  crowd: NavCrowd;
  /** The interaction fired on an NPC's id (the things' hook calls it). */
  interact(id: string): void;
  /** Put an NPC down (a cutscene's last mark): at `pos`, facing `yaw`, its routine walking it on from there. */
  place(id: string, pos: [number, number, number], yaw: number): void;
  /** Whether an interactable id is an NPC's. */
  owns(id: string): boolean;
  /** Steps run (diagnostics). */
  steps(): number;
}

const AGENT_SPEC = { radius: 0.3, height: 1.6, maxAccel: 6, separation: 0.4 };
const TURN_RATE = 6;
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

export function createTownsfolk(host: TownHost): Townsfolk {
  const { content, places, nav } = host;
  const defs = new Map(content.npcs.map((n) => [n.id, n]));
  const agentOf = new Map(content.npcs.map((n, i) => [n.id, i + 1]));
  const HERO_AGENT = 0;
  const OTHERS = 1000;
  const schedule = new RoutineSchedule(Object.fromEntries(content.npcs.map((n) => [n.id, n.routine])));
  const crowd = new NavCrowd(nav, { maxAgents: content.npcs.length + 24, maxAgentRadius: 0.5, replanEvery: 1 });
  let world: World | null = null;
  let entity = -1;
  /** The crowd as the build left it (a fresh slice restores it). */
  let initialCrowd: CrowdSnapshot | null = null;
  let steps = 0;
  const state = (): TownState => {
    if (!world) throw new Error("town: not built yet");
    return world.getComponent<TownState>(entity, TOWN_C)!;
  };
  const npc = (id: string): NpcState => {
    const n = state().npcs.find((x) => x.id === id);
    if (!n) throw new Error(`town: no npc "${id}"`);
    return n;
  };
  const tileLive = (x: number, z: number): boolean => nav.hasTile(nav.tileAt(x, z));
  const hourGap = (now: number, last: number | null): number => (last === null ? 0 : (((now - last) % 24) + 24) % 24);

  /** Where an NPC starts on a fresh game: on its routine's spot for the clock's hour. */
  const fresh = (): TownState => {
    const npcs: NpcState[] = content.npcs.map((n) => {
      const h = places.place("home", n.id)?.spots[0] ?? { x: 0, z: 0 };
      return { id: n.id, pos: [h.x, host.groundAt(h.x, h.z), h.z], yaw: 0, vel: [0, 0], mode: "virtual", routine: { block: null, place: null, spot: 0, moves: 0, arrived: false, since: 0, nextMove: null }, activity: null, talk: null };
    });
    // a new game snaps everyone to the hour on its first step
    return { t: 0, lastHour: null, npcs, talks: 0, snaps: 0 };
  };

  const addAgent = (n: NpcState): void => {
    const id = agentOf.get(n.id)!;
    if (crowd.has(id)) return;
    const near = nav.nearest(n.pos, [1.5, 3, 1.5]);
    const at = near ? near.point : n.pos;
    crowd.add(id, at, { ...AGENT_SPEC, maxSpeed: defs.get(n.id)!.walk * 1.15 });
    n.pos = [at[0], at[1], at[2]];
  };
  const dropAgent = (n: NpcState): void => {
    const id = agentOf.get(n.id)!;
    if (crowd.has(id)) crowd.remove(id);
  };

  const interactId = (id: string): string => `${NPC_PREFIX}${id}`;
  const syncInteraction = (s: TownState): void => {
    const reg = host.interaction();
    for (const n of s.npcs) {
      const iid = interactId(n.id);
      const pos = { x: n.pos[0], y: n.pos[1], z: n.pos[2] };
      const enabled = n.mode !== "inside" && n.talk === null;
      if (!reg.get(iid)) reg.register({ id: iid, verb: "verb.talk", pos, radius: 1.9, priority: 4, enabled });
      else reg.set(iid, { pos, enabled });
    }
  };

  let events: Parameters<GameModule<GameEvents>["setup"]>[0]["events"] | null = null;

  const step = (dt: number): void => {
    const s = state();
    steps++;
    s.t += dt;
    const hour = host.hours();
    const snap = s.lastHour === null || hourGap(hour, s.lastHour) > SNAP_HOURS;
    if (snap) s.snaps++;
    s.lastHour = hour;
    // talks run out on their cap (a conversation the dialogue never closed)
    for (const n of s.npcs) if (n.talk && s.t >= n.talk.until) n.talk = null;
    const agents: RoutineAgent[] = s.npcs.map((n) => ({ id: n.id, x: n.pos[0], z: n.pos[2], busy: n.talk !== null }));
    const states: Record<string, RoutineState> = Object.fromEntries(s.npcs.map((n) => [n.id, n.routine]));
    const decisions = stepRoutines(agents, states, schedule, { hour, now: s.t, place: (id, who) => places.place(id, who), snap });
    for (const n of s.npcs) {
      n.routine = states[n.id]!;
      const d = decisions[n.id]!;
      const def = defs.get(n.id)!;
      // put on its spot at once (a clock jump)
      if (d.snap && d.goal) {
        n.pos = [d.goal.x, host.groundAt(d.goal.x, d.goal.z), d.goal.z];
        if (d.goal.yaw !== undefined) n.yaw = d.goal.yaw;
        n.vel = [0, 0];
        if (crowd.has(agentOf.get(n.id)!)) {
          dropAgent(n);
          if (!d.inside) addAgent(n);
        }
      }
      // indoors: out of sight, off the crowd, on its spot
      if (d.inside) {
        if (n.mode !== "inside") {
          dropAgent(n);
          n.mode = "inside";
          n.vel = [0, 0];
        }
        n.activity = d.activity;
        continue;
      }
      if (n.mode === "inside") n.mode = "virtual";
      // on the crowd while its ground's tile is built; virtual otherwise
      const live = tileLive(n.pos[0], n.pos[2]);
      if (n.mode === "live" && !live) {
        dropAgent(n);
        n.mode = "virtual";
      } else if (n.mode === "virtual" && live) {
        addAgent(n);
        n.mode = "live";
      }
      const agent = agentOf.get(n.id)!;
      const goal = n.talk ? null : d.goal;
      const there = d.arrived;
      n.activity = there && !n.talk ? d.activity : null;
      if (n.mode === "live") {
        if (!goal || there) crowd.stop(agent);
        else crowd.moveTo(agent, [goal.x, host.groundAt(goal.x, goal.z), goal.z], 0.15, def.walk);
      } else if (goal && !there) {
        // virtual: a straight line at its pace, on the ground
        const dx = goal.x - n.pos[0];
        const dz = goal.z - n.pos[2];
        const l = Math.hypot(dx, dz);
        const k = Math.min(1, (def.walk * dt) / (l || 1));
        n.pos = [n.pos[0] + dx * k, 0, n.pos[2] + dz * k];
        n.pos[1] = host.groundAt(n.pos[0], n.pos[2]);
        n.vel = l > 1e-6 ? [(dx / l) * def.walk, (dz / l) * def.walk] : [0, 0];
        n.yaw = Math.atan2(dx, dz);
      } else n.vel = [0, 0];
    }
    // the crowd: the hero and the enemies placed for the others to avoid, then a step
    const h = host.hero();
    if (!crowd.has(HERO_AGENT)) crowd.add(HERO_AGENT, [h.x, h.y, h.z], { radius: 0.3, height: 1.7, maxSpeed: 8, maxAccel: 40 }, { external: true });
    crowd.place(HERO_AGENT, [h.x, h.y, h.z], [h.vx, 0, h.vz]);
    const others = host.others();
    for (let i = 0; i < 16; i++) {
      const id = OTHERS + i;
      const o = others[i];
      if (o) {
        if (!crowd.has(id)) crowd.add(id, [o.x, o.y, o.z], { radius: 0.45, height: 1.6, maxSpeed: 8, maxAccel: 40 }, { external: true });
        crowd.place(id, [o.x, o.y, o.z], [0, 0, 0]);
      } else if (crowd.has(id)) crowd.remove(id);
    }
    if (s.npcs.some((n) => n.mode === "live")) crowd.step(dt);
    for (const n of s.npcs) {
      if (n.mode !== "live") continue;
      const a = crowd.agent(agentOf.get(n.id)!)!;
      n.pos = [a.pos[0], a.pos[1], a.pos[2]];
      n.vel = [a.vel[0], a.vel[2]];
    }
    // facing: the way it walks, else its spot's way, else the hero it talks to
    for (const n of s.npcs) {
      if (n.mode === "inside") continue;
      const speed = Math.hypot(n.vel[0], n.vel[1]);
      let want: number | null = null;
      if (n.talk) want = Math.atan2(n.talk.face[0] - n.pos[0], n.talk.face[1] - n.pos[2]);
      else if (speed > 0.2) want = Math.atan2(n.vel[0], n.vel[1]);
      else if (n.routine.arrived) {
        const p = n.routine.place ? places.place(n.routine.place.startsWith("home:") ? "home" : n.routine.place, n.id) : null;
        const spot = p?.spots[n.routine.spot];
        if (spot?.yaw !== undefined) want = spot.yaw;
      }
      if (want !== null) n.yaw = wrap(n.yaw + wrap(want - n.yaw) * Math.min(1, TURN_RATE * dt));
    }
    syncInteraction(s);
  };

  const self: Townsfolk = {
    hashed: TOWN_HASHED,
    schedule,
    crowd,
    state,
    npc,
    def: (id) => {
      const d = defs.get(id);
      if (!d) throw new Error(`town: no npc "${id}"`);
      return d;
    },
    owns: (id) => id.startsWith(NPC_PREFIX) && defs.has(id.slice(NPC_PREFIX.length)),
    steps: () => steps,
    interact(id) {
      const n = npc(id.startsWith(NPC_PREFIX) ? id.slice(NPC_PREFIX.length) : id);
      const s = state();
      const h = host.hero();
      n.talk = { until: s.t + (host.talkCap ?? 30), face: [h.x, h.z] };
      n.activity = null;
      if (n.mode === "live") crowd.stop(agentOf.get(n.id)!);
      n.vel = [0, 0];
      s.talks++;
      events?.emit("npc:interact", { npc: n.id });
    },
    place(id, pos, yaw) {
      const n = npc(id);
      n.pos = [pos[0], pos[1], pos[2]];
      n.yaw = yaw;
      n.vel = [0, 0];
      n.routine.arrived = false;
      const agent = agentOf.get(id)!;
      if (crowd.has(agent)) crowd.teleport(agent, n.pos);
    },
    build(w) {
      world = w;
      entity = w.createEntity();
      w.addComponent(entity, TOWN_C, fresh());
      initialCrowd = crowd.snapshot();
    },
    module: {
      id: "town",
      version: "0.1.0",
      // checked declarations (decision 0077): what it emits
      provides: { events: ["npc:interact"] },
      setup(ctx) {
        events = ctx.events;
        ctx.events.on("dialogue:close", ({ npc: id }) => {
          if (!defs.has(id) || !world) return;
          npc(id).talk = null;
        });
        ctx.registerSystem(
          ({ dt }) => {
            ROUTINE_FAULTS.hourIgnored = TOWN_FAULTS.routineHourIgnored;
            step(dt);
          },
          { name: "town:npcs", order: 55 },
        );
        ctx.registerSaveSlice({
          serialize: () => ({ state: structuredClone(state()), crowd: crowd.snapshot() }),
          deserialize: (data) => {
            // a save from before the town joined the valley (the v6 migration's marker): a new game's town
            const d = isFresh(data) ? { state: fresh(), crowd: initialCrowd! } : (data as { state: TownState; crowd: CrowdSnapshot });
            // the component keeps its object (the hash and the view read it): its fields replaced
            Object.assign(state(), structuredClone(d.state));
            crowd.restore(d.crowd);
          },
        });
      },
    },
  };
  // the facts (seam 2): the clock's hour and where each NPC is
  host.facts.register({ name: "time", read: (p) => (p.length === 1 && p[0] === "hours" ? host.hours() : undefined), keys: () => ["hours"] });
  host.facts.register({
    name: "npc",
    read: (p) => {
      if (p.length !== 2 || p[1] !== "place" || !defs.has(p[0]!) || !world) return undefined;
      const at = placeOf(npc(p[0]!).routine);
      return at && at.startsWith("home:") ? "home" : at;
    },
    keys: () => content.npcs.map((n) => `${n.id}.place`),
  });
  return self;
}
